import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  preflightEmailChange, changeUserEmail,
  type EmailChangePreflight,
} from '@/lib/workerApi';
import { UserEmailHistory } from '@/components/UserEmailHistory';

/**
 * Change a user's sign-in email, for support.
 *
 * Deliberately two steps. The address is typed, checked against every
 * uniqueness constraint in the system, and only then is the write offered —
 * because the write cannot be undone by the person it happens to: if the new
 * address is wrong they cannot receive the mail that would let them tell us.
 *
 * The double entry is not ceremony either. On the brand side the verification
 * gate blocks the entire authenticated app, so an address typo here turns
 * "wrong email on file" into "paying customer locked out of their dashboard".
 */
export function ChangeUserEmailDialog({
  user, open, onOpenChange,
}: {
  user: { id: string; email?: string | null; full_name?: string | null; tiktok_handle?: string | null } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [reason, setReason] = useState('');
  const [allowTrolley, setAllowTrolley] = useState(false);
  const [pre, setPre] = useState<EmailChangePreflight | null>(null);

  const reset = () => {
    setEmail1(''); setEmail2(''); setReason(''); setAllowTrolley(false); setPre(null);
  };

  const close = (next: boolean) => { if (!next) reset(); onOpenChange(next); };

  const bothMatch = email1.trim().length > 0 && email1.trim().toLowerCase() === email2.trim().toLowerCase();

  const checkMutation = useMutation({
    mutationFn: () => preflightEmailChange(user!.id, email1.trim()),
    onSuccess: setPre,
    onError: (e: Error) => toast.error(e.message),
  });

  const applyMutation = useMutation({
    mutationFn: () => changeUserEmail({
      userId: user!.id,
      newEmail: email1.trim(),
      reason: reason.trim(),
      allowTrolleyLinked: allowTrolley,
    }),
    onSuccess: (res) => {
      toast.success(`Email changed to ${res.new_email}`);
      // Every list that renders an email, plus anything keyed on the profile.
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['creator-users'] });
      qc.invalidateQueries({ queryKey: ['user-email-history', user!.id] });
      close(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The preflight is the gate. It re-runs server-side inside the write's
  // transaction too, so a stale "ok" here cannot push a conflicting change
  // through — this only decides what the button looks like.
  const trolleyLinked = !!pre?.target?.trolley_recipient_id;
  const canApply =
    pre?.ok === true &&
    bothMatch &&
    reason.trim().length >= 10 &&
    (!trolleyLinked || allowTrolley) &&
    !applyMutation.isPending;

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Change sign-in email</DialogTitle>
          <DialogDescription>
            {user.full_name || user.tiktok_handle || 'This user'} currently signs in as{' '}
            <strong className="text-foreground">{user.email}</strong>. Their password is not
            affected, but every session ends and they must sign in again with the new address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Prior changes, before this one is made. An account whose address
              has already moved twice is a different proposition from one that
              never has, and that is worth knowing BEFORE typing, not after. */}
          <div className="rounded-md border bg-muted/20 p-2.5">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Previous email changes</p>
            <UserEmailHistory userId={user.id} compact />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-email-1">New email</Label>
            <Input
              id="new-email-1" type="email" autoComplete="off" placeholder="name@example.com"
              value={email1}
              onChange={(e) => { setEmail1(e.target.value); setPre(null); }}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-email-2">Type it again</Label>
            <Input
              id="new-email-2" type="email" autoComplete="off" placeholder="name@example.com"
              value={email2}
              onChange={(e) => setEmail2(e.target.value)}
            />
            {email2.length > 0 && !bothMatch && (
              <p className="text-xs text-destructive">The two addresses don&apos;t match.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="change-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea
              id="change-reason" rows={2} value={reason}
              placeholder="e.g. Typo at signup — creator asked in ticket #123"
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Goes in the audit log. It is the only explanation anyone reading this later will have.
            </p>
          </div>

          {/* ── Preflight result ─────────────────────────────────────────── */}
          {pre && pre.ok && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-green-800">
                <CheckCircle2 className="h-4 w-4" /> No conflicts — safe to apply
              </div>
              {pre.will_revoke && (
                <ul className="mt-2 space-y-0.5 text-xs text-green-900">
                  <li>Signs them out of every device</li>
                  {pre.will_revoke.pending_otp_codes > 0 && (
                    <li>Cancels {pre.will_revoke.pending_otp_codes} pending verification code(s) sent to the old address</li>
                  )}
                  {pre.will_revoke.coach_sign_in_links > 0 && (
                    <li>Revokes {pre.will_revoke.coach_sign_in_links} live coach sign-in link(s) for the old address</li>
                  )}
                  {pre.will_revoke.queued_emails > 0 && (
                    <li>Redirects {pre.will_revoke.queued_emails} undelivered email(s) to the new address</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {pre && !pre.ok && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {pre.error === 'unchanged' ? 'Already their address'
                  : pre.error === 'invalid_email' ? 'Not a valid address'
                  : pre.error === 'user_not_found' ? 'User not found'
                  : `${pre.blocking_count} conflict(s) — cannot apply`}
              </div>
              {pre.message && <p className="mt-1 text-xs text-muted-foreground">{pre.message}</p>}
              <ul className="mt-2 space-y-1 text-xs">
                {(pre.conflicts ?? []).filter(c => c.blocking).map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <Badge variant="outline" className="font-mono text-[10px]">{c.table}</Badge>
                    <span className="text-muted-foreground">
                      already uses this address
                      {/* The question the admin actually has to answer: is the
                          blocker a throwaway re-registration or a real account? */}
                      {c.other_account_is_empty === true && ' — that account looks empty (likely a duplicate signup)'}
                      {c.other_account_is_empty === false && ' — that account has real activity'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trolley is a warning, not a blocker — but it has to be taken
              deliberately, because create-trolley-recipient adopts an existing
              Trolley recipient found by email. */}
          {pre?.ok && trolleyLinked && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-amber-900">
                <ShieldAlert className="h-4 w-4" /> This creator is linked to a Trolley payout account
              </div>
              <p className="mt-1 text-xs text-amber-900">
                If the new address already exists in Trolley, their profile can be bound to someone
                else&apos;s payout account. Check with finance before overriding.
              </p>
              <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                <Checkbox checked={allowTrolley} onCheckedChange={(v) => setAllowTrolley(v === true)} />
                I understand — change it anyway
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => checkMutation.mutate()}
            disabled={!bothMatch || checkMutation.isPending}
          >
            {checkMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Check for conflicts
          </Button>
          <Button
            variant="destructive"
            onClick={() => applyMutation.mutate()}
            disabled={!canApply}
            title={pre ? undefined : 'Run the conflict check first'}
          >
            {applyMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Change email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
